import { FC, useState } from 'react'
import { getDateTimeFromUtc, getUserAvatar, reactStringReplace, sanitizeUrl, useStores } from '@/utils'
import { IAnswerBase, IQuestionBase, MessageType } from '@/types'
import { Avatar } from '@/components/atoms/Avatar'
import { EditAndDeleteButtons } from '@/components/atoms/EditAndDeleteButtons'
import { UpdateAnswerForm } from '@/components/molecules/UpdateAnswerForm'
import Link from 'next/link'
import { observer } from 'mobx-react'
import { ReplyCard } from '@/components/molecules/ReplyCard'
import classNames from 'classnames'
import { ReplyModal } from '@/components/organisms/ReplyModal'
import { ReplyButtonGroup } from '@/components/molecules/ReplyButtonGroup'
import { URL_PATTERN } from '@/constants'
import styles from './index.module.scss'

type Props = {
  question: IQuestionBase
  answer: IAnswerBase
  answers: IAnswerBase[]
  setAnswers: (answers: IAnswerBase[]) => void
  onClickDelete?: (id: string) => void
  haveEditAndDelete?: boolean
  parentReply: IAnswerBase
  setParentReply: (parentReply: IAnswerBase) => void
  isQuestioner: boolean
}

export const AnswerCard: FC<Props> = observer(
  ({
    question,
    answer,
    answers,
    setAnswers,
    onClickDelete,
    haveEditAndDelete,
    parentReply,
    setParentReply,
    isQuestioner,
  }) => {
    const { questions, viewer, messages } = useStores()
    const [edit, setEdit] = useState<boolean>(false)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [childReplies, setChildReplies] = useState<IAnswerBase[]>(answer?.childReplies)
    const [isOpenMoreReplies, setIsOpenMoreReplies] = useState<boolean>(false)

    const deleteReply = async (id: string) => {
      // eslint-disable-next-line no-alert
      if (window.confirm('Are you sure to delete this reply?')) {
        const isSuccessful = await viewer.viewer.removeMyAnswer(id)
        if (isSuccessful) {
          setChildReplies(childReplies.filter((r) => r.id !== id))

          messages.add({
            type: MessageType.Info,
            body: 'Reply deleted.',
            isTranslated: true,
            ttl: 5000,
          })
        }
      }
    }

    const toggleModal = () => {
      setParentReply(answer)
      setIsModalOpen(!isModalOpen)
    }

    const hasChild = answer?.repliesCount > 0

    const setReply = async () => {
      const reply = await questions.fetchReply({ id: answer.id })
      setChildReplies(reply?.childReplies)
      setIsOpenMoreReplies(true)
    }

    const className = classNames(styles.container, {
      [styles.hasChild]: hasChild,
    })

    return (
      <>
        <div className={className}>
          <div className={styles.heading}>
            <div className={styles.respondentGroup}>
              <Link href={`/users/${answer?.respondent?.username}`}>
                <a>
                  <div className={styles.avatar}>
                    <Avatar src={getUserAvatar(answer?.respondent)} alt={answer?.respondent?.name} />
                  </div>
                </a>
              </Link>
              <div>
                <div className={styles.name}>{answer?.respondent?.name}</div>
                <div className={styles.time}>{getDateTimeFromUtc(answer?.updatedAt, 'YYYY/M/D HH:mm')}</div>
              </div>
            </div>
            {haveEditAndDelete && !edit && (
              <EditAndDeleteButtons
                onClickEdit={() => {
                  setEdit(true)
                }}
                onClickDelete={() => onClickDelete(answer.id)}
              />
            )}
          </div>
          {edit ? (
            <div className={styles.form}>
              <UpdateAnswerForm
                answer={answer}
                answers={answers}
                setAnswers={(newAnswers: IAnswerBase[]) => setAnswers(newAnswers)}
                clickCancel={() => {
                  setEdit(false)
                }}
                setEdit={setEdit}
              />
            </div>
          ) : (
            <p className={styles.body}>
              {reactStringReplace(answer?.body, URL_PATTERN, (match, i) => (
                <a key={i} href={sanitizeUrl(match)} target='_blank' rel='noreferrer'>
                  {match}
                </a>
              ))}
            </p>
          )}
          <ReplyButtonGroup
            toggleModal={toggleModal}
            respondent={answer?.respondent}
            answerId={answer?.id}
            amountOfThanks={Number(answer.amount)}
          />
          {hasChild && !isOpenMoreReplies && (
            <div className={styles.viewMore} role='button' tabIndex={0} onClick={setReply} onKeyPress={setReply}>
              View more replies
            </div>
          )}
        </div>
        {((!hasChild && childReplies?.length > 0) || (hasChild && isOpenMoreReplies && childReplies?.length > 0)) && (
          <div>
            {childReplies?.map((r) => {
              return (
                <ReplyCard
                  key={r.id}
                  reply={r}
                  replies={childReplies}
                  setChildReplies={(newChildReplies: IAnswerBase[]) => setChildReplies(newChildReplies)}
                  questionerName={question.questioner.username}
                  onClickDelete={(id: string) => deleteReply(id)}
                  haveEditAndDelete={r.respondent?.username === viewer.viewer?.username}
                  parentReply={parentReply}
                  setParentReply={(newParentReply: IAnswerBase) => setParentReply(newParentReply)}
                  isQuestioner={isQuestioner}
                />
              )
            })}
          </div>
        )}
        <ReplyModal
          parentReply={parentReply}
          childReplies={childReplies}
          setReplies={(newReplies: IAnswerBase[]) => setChildReplies(newReplies)}
          isQuestioner={isQuestioner}
          isModalOpen={isModalOpen}
          setIsModalOpen={(isOpen: boolean) => setIsModalOpen(isOpen)}
        />
      </>
    )
  }
)
